import { TempControlFacilityType, TempControlSizingMethod } from '__generated-api__';
import { isMaxUseFacility } from 'tempcontrol/utils/calculation-method';

export interface EnumOption<TValue extends string> {
  value: TValue;
  label: string;
  description?: string;
}

// Listed alphabetically per the copy doc; `value` carries the facility so
// display order is independent of the enum.
export const facilityTypeOptions: EnumOption<TempControlFacilityType>[] = [
  {
    value: TempControlFacilityType.Apartment,
    label: 'Apartment / Multifamily',
    description: 'Residential dwellings with shared hot water service.',
  },
  {
    value: TempControlFacilityType.Club,
    label: 'Club / Recreation',
    description: 'Country clubs, recreation centers.',
  },
  {
    value: TempControlFacilityType.Gym,
    label: 'Gym / Locker Room',
    description: 'Fitness facilities and high-volume shower groups.',
  },
  {
    value: TempControlFacilityType.Hospital,
    label: 'Healthcare / Hospital',
    description: 'Patient rooms, staff facilities, exam rooms.',
  },
  {
    value: TempControlFacilityType.Hotel,
    label: 'Hotel / Lodging',
    description: 'Guest bath groups served by a central plant.',
  },
  {
    value: TempControlFacilityType.Industrial,
    label: 'Industrial / Manufacturing',
    description: 'Plants, warehouses, processing facilities.',
  },
  {
    value: TempControlFacilityType.MaximumFlow,
    label: 'Maximum Flow',
    description: 'Continuous-use applications where all fixtures run simultaneously.',
  },
  {
    value: TempControlFacilityType.Office,
    label: 'Office',
    description: 'Office buildings, professional facilities.',
  },
  {
    value: TempControlFacilityType.School,
    label: 'School / Dormitory',
    description: 'K-12, colleges/universities, residence halls.',
  },
];

/**
 * Add-on options that build the valve part-number suffix. They do NOT affect
 * sizing - server-side `HuntersMethod::applyOptions()` only appends SKU
 * suffixes (A/W/-ASB/-1/-W/-TOP/-V) - so they are chosen on the results page
 * next to the recommended product rather than on the sizing config step.
 * Listed alphabetically with Title Case labels per client QA.
 */
export const addOnOptionItems = [
  {
    key: 'cold_water_bypass',
    label: 'Cold Water Bypass',
    description: 'Adds a valved bypass loop for emergency or system maintenance.',
  },
  {
    key: 'factory_assembled',
    label: 'Factory Assembled',
    description: 'Professionally assembled and packaged by Symmons.',
  },
  {
    key: 'piping_package',
    label: 'Piping Package',
    description: 'Includes brass piping and unions for the valve assembly.',
  },
  {
    key: 'top_supplies',
    label: 'Top Supplies',
    description: 'Routes hot/cold supply lines from the top of the assembly.',
  },
  {
    key: 'vacuum_breaker',
    label: 'Vacuum Breaker',
    description: 'Anti-siphon protection for cross-connection control.',
  },
  {
    key: 'wall_mounts',
    label: 'Wall Mounts',
    description: 'Wall-mounting kit for surface-mounted assemblies.',
  },
] as const;

export type AddOnOptionKey = (typeof addOnOptionItems)[number]['key'];

/**
 * Order matches `TempControlFixtureType` integer indices 0..13 from the spec.
 * The wizard renders these as configurable rows on step 4.
 */
export const fixtureTypeLabels: ReadonlyArray<{ index: number; label: string; group: string }> = [
  { index: 0, label: 'Lavatory Faucet (Private)', group: 'Lavatories' },
  { index: 1, label: 'Lavatory Faucet (Public)', group: 'Lavatories' },
  { index: 2, label: 'Bathtub', group: 'Bathing' },
  { index: 3, label: 'Therapeutic Bath', group: 'Bathing' },
  { index: 4, label: 'Kitchen Sink', group: 'Sinks' },
  { index: 5, label: 'Pantry Sink', group: 'Sinks' },
  { index: 6, label: 'Service Sink', group: 'Sinks' },
  { index: 7, label: 'Shower', group: 'Bathing' },
  { index: 8, label: 'Circular Wash Fountain', group: 'Wash Fountains' },
  { index: 9, label: 'Semi-Circular Wash Fountain', group: 'Wash Fountains' },
  { index: 10, label: 'Residential Dishwasher', group: 'Appliances' },
  { index: 11, label: 'Commercial Dishwasher', group: 'Appliances' },
  { index: 12, label: 'Residential Washing Machine', group: 'Appliances' },
  { index: 13, label: 'Commercial Washing Machine', group: 'Appliances' },
];

/**
 * Fixture indices shown by default on step 4, in display order (shower and
 * lavatories first, matching the legacy TempSize fixture listing).
 */
export const primaryFixtureIndices: ReadonlyArray<number> = [7, 0, 1, 2, 4];

const fixtureLabelByIndex = new Map(fixtureTypeLabels.map((fixture) => [fixture.index, fixture.label]));

/** Resolve step-4 rows for the given fixture indices. */
export const fixtureRowsForIndices = (indices: ReadonlyArray<number>) =>
  indices
    .map((index) => {
      const label = fixtureLabelByIndex.get(index);
      return typeof label === 'string' ? { idx: index, label } : null;
    })
    .filter((row): row is { idx: number; label: string } => row !== null);

/**
 * Choose the internal flow-derivation method (`hunter` / `max_use` / `mixing_valve`)
 * based on the user-selected facility type. Mirrors the legacy C# `Project.cs`
 * facility-to-method routing - every non-Maximum Flow facility runs through
 * Hunter's curve while Maximum Flow uses the additive max-use derivation.
 *
 * Mixing-valve method is reserved for future facility variants.
 */
export const sizingMethodForFacility = (facility: TempControlFacilityType): TempControlSizingMethod => {
  if (facility === TempControlFacilityType.MaximumFlow) {
    return TempControlSizingMethod.MaxUse;
  }
  return TempControlSizingMethod.Hunter;
};

/**
 * True when the facility sizes on the additive "Max Use" flow method rather
 * than the probabilistic Hunter's ASME curve, and therefore needs per-fixture
 * flow rates. These map to `Curve::MaxFlow` in the engine (see
 * `FacilityType::asmeCurve()`), which derives peak flow as `Σ quantity ×
 * flow_rate` - so the wizard MUST collect a per-fixture flow rate (GPM) for
 * them, or the engine has no demand to size against. Drives the extra GPM
 * inputs on step 4 and their validation.
 *
 * Delegates to {@see isMaxUseFacility} so the frontend has a single source of
 * truth; kept in lockstep with the backend `FacilityType::usesMaxUseFlow()`.
 */
export const facilityUsesFlowRate = (facility?: TempControlFacilityType | null): boolean => isMaxUseFacility(facility);

/**
 * Default allowable PSI drop. The engineering spec lists 10 PSI as the
 * universal default across every facility type; the user can still override.
 */
export const defaultAllowablePsi = (): number => 10;

/**
 * Numbered wizard steps as shown to the user (`STEP X / 3`).
 *
 * The valve family (Digital vs Conventional) is chosen on the results page,
 * not as its own step. Results is intentionally NOT numbered - the design
 * treats it as a separate page that lives outside the wizard counter, so the
 * API caps `current_step` at 3 and we render the results view as its own screen.
 */
export const wizardSteps = [
  { id: 1 as const, slug: 'project', title: 'Name Your Project' },
  { id: 2 as const, slug: 'configuration', title: 'Add Property Details' },
  { id: 3 as const, slug: 'fixtures', title: 'Add Fixture Counts' },
] as const;

export type WizardStepId = (typeof wizardSteps)[number]['id'];

/**
 * Per-step copy for the left intro column. Mirrors the design's
 * "STEP 2 / 3 — Project configuration" + body block.
 */
export const wizardStepCopy: Record<WizardStepId, { title: string; body: string }> = {
  1: {
    title: 'Name Your Project',
    body: 'Create a name for your project to continue. This project name will display on the final specification sheet.',
  },
  2: {
    title: 'Add Property Details',
    body: 'Provide the building’s type and valve requirements.',
  },
  3: {
    title: 'Add Fixture Counts',
    body: 'For each type of plumbing fixture, enter the amount served by this mixing valve. The sizing tool calculates the total demand and then recommends the correct valve.',
  },
};

/** Persistent hero copy shown above every wizard screen. */
export const wizardHero = {
  eyebrow: 'Sizing your TempControl mixing valve',
  title: 'TempControl® Sizing Tool',
  body: 'Whether you need a digital mixing valve with remote access or a traditional thermostatic version, this step-by-step process will help you choose the correct TempControl valve. Just add the building and piping details to generate a downloadable specification sheet of the correct valve for your project.',
};
